// ─── Emploi du temps (données PARTAGÉES avec MoPCal) ──────────────
// Porté de mop-cal-v3/services/emploiDuTemps.js — structure INCHANGÉE :
//   emploiDuTemps/{calId}/grilles/{grilleId}
//     { nom, debut, fin, actif, cases: { '<jourIdx>_<creneauIdx>':
//       { matiere, salle, couleur, span? } }, heureDebut, heureFin, dureeMin, maj }
// Différence de CONSOMMATION : la suite GÉNÈRE les occurrences à la volée
// (type 'edt', jamais écrites en base) ; MoPCal matérialisait des événements
// (source:'edt') — dédupliqués côté écran (jours déjà matérialisés ignorés).
import { db, journaliser } from '@mop/core';
import {
  doc, collection, onSnapshot, addDoc, deleteDoc, getDoc, getDocs, updateDoc,
} from 'firebase/firestore';

export const JOURS_EDT = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven'];
export const CRENEAUX_EDT = [
  { debut: '08:00', fin: '09:00' }, { debut: '09:00', fin: '10:00' },
  { debut: '10:00', fin: '11:00' }, { debut: '11:00', fin: '12:00' },
  { debut: '14:00', fin: '15:00' }, { debut: '15:00', fin: '16:00' },
  { debut: '16:00', fin: '17:00' },
];
export const COULEURS_EDT = ['#6C4FE6', '#E91E63', '#0277BD', '#2E7D32', '#EF6C00', '#8D6E63', '#607D8B'];
export const PLAGE_DEFAUT = { heureDebut: 8, heureFin: 17, dureeMin: 60 };
export const DUREES_EDT = [15, 30, 45, 60, 90, 120, 180, 240];

const minToHHMM = (m) => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
const _hhmm = (s) => { const [h, m] = String(s).split(':').map(Number); return (h || 0) * 60 + (m || 0); };

export function libelleDuree(min) {
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60), m = min % 60;
  return m ? `${h} h ${String(m).padStart(2, '0')}` : `${h} h`;
}

// Créneaux d'une grille : calculés depuis heureDebut/heureFin/dureeMin si
// configurés ; sinon repli sur le jeu fixe historique.
export function creneauxDe(grille) {
  const hd = grille?.heureDebut, hf = grille?.heureFin, dm = grille?.dureeMin;
  if (hd == null || hf == null || !dm || hf <= hd) return CRENEAUX_EDT;
  const slots = [];
  const fin = hf * 60;
  let t = hd * 60, garde = 0;
  while (t < fin && garde++ < 300) {
    const e = Math.min(t + dm, fin);
    slots.push({ debut: minToHHMM(t), fin: minToHHMM(e) });
    t = e;
  }
  return slots.length ? slots : CRENEAUX_EDT;
}

export const cleCase = (jourIdx, creneauIdx) => `${jourIdx}_${creneauIdx}`;

// Remappe les cases quand la plage/durée change : le contenu suit les HORAIRES.
export function migrerCasesConfig(cases, ancien, nouveau) {
  if (!cases) return {};
  const meme = ancien.length === nouveau.length
    && ancien.every((c, i) => c.debut === nouveau[i].debut && c.fin === nouveau[i].fin);
  if (meme) return cases;
  const out = {};
  for (const key of Object.keys(cases)) {
    const [jiStr, iStr] = key.split('_');
    const ji = Number(jiStr), i = Number(iStr);
    const c = cases[key];
    if (!c || !(c.nom || c.matiere) || i >= ancien.length) continue;
    const span = Math.max(1, c.span || 1);
    // Les heures RÉELLES du cours ne changent JAMAIS (figées depuis les
    // anciennes bornes si absentes) — seul l'ancrage clé/span est recalculé.
    const debutMin = c.heure ? _hhmm(c.heure) : _hhmm(ancien[i].debut);
    const finMin = c.heureFin ? _hhmm(c.heureFin) : _hhmm(ancien[Math.min(i + span - 1, ancien.length - 1)].fin);
    let nStart = nouveau.findIndex(cr => _hhmm(cr.debut) <= debutMin && debutMin < _hhmm(cr.fin));
    if (nStart < 0) nStart = nouveau.findIndex(cr => _hhmm(cr.debut) >= debutMin);
    if (nStart < 0) continue;
    let nSpan = 0;
    for (let j = nStart; j < nouveau.length && _hhmm(nouveau[j].debut) < finMin; j++) nSpan++;
    if (nSpan < 1) nSpan = 1;
    const { span: _drop, ...contenu } = c;
    let k = nStart;                        // deux cours dans le même créneau : clé décalée
    while (out[cleCase(ji, k)] && k < nouveau.length - 1) k++;
    out[cleCase(ji, k)] = { ...contenu, heure: minToHHMM(debutMin), heureFin: minToHHMM(finMin), ...(nSpan > 1 ? { span: nSpan } : {}) };
  }
  return out;
}

// ─── Grilles ──────────────────────────────────────────────────────
const grillesRef = (calId) => collection(db, 'emploiDuTemps', calId, 'grilles');

// `onErreur(message)` remonté — même correction que sur les contacts/le
// journal/la messagerie : un refus de règle donnait « aucun emploi du temps »
// en silence, indiscernable d'un calendrier qui n'en a réellement aucun.
export function ecouterGrillesEDT(calId, callback, onErreur) {
  if (!calId) { callback([]); return () => {}; }
  return onSnapshot(grillesRef(calId), (snap) => {
    const liste = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    liste.sort((a, b) => (a.debut || '').localeCompare(b.debut || '') || (a.nom || '').localeCompare(b.nom || ''));
    callback(liste);
  }, (e) => { callback([]); onErreur?.(e?.message || String(e)); });
}

// `auteur` optionnel = { uid, nom } — présent, la création est journalisée
// (même patron que MoPCal `services/emploiDuTemps.js`).
export async function creerGrilleEDT(calId, { nom = 'Emploi du temps', debut = '', fin = '', cases = {}, actif = true, heureDebut, heureFin, dureeMin } = {}, auteur) {
  const data = { nom, debut, fin, cases, actif, maj: new Date().toISOString() };
  if (heureDebut != null) data.heureDebut = heureDebut;
  if (heureFin != null) data.heureFin = heureFin;
  if (dureeMin != null) data.dureeMin = dureeMin;
  const ref = await addDoc(grillesRef(calId), data);
  if (auteur) journaliser(calId, { type: 'edt', action: 'creation', titre: nom || '', par: auteur.uid || null, parNom: auteur.nom || '' });
  return ref.id;
}

// updateDoc (PAS setDoc merge) : la map `cases` doit être REMPLACÉE (sinon les
// suppressions de clés/`span` seraient perdues par la fusion récursive).
// `auteur` optionnel = { uid, nom, titre } — SEULEMENT si fourni : les
// enregistrements fréquents et granulaires (édition d'une cellule) restent
// silencieux, seuls les changements structurels (passés avec `auteur`, ex.
// activation/désactivation) sont tracés. Même patron que MoPCal.
export async function enregistrerGrilleEDT(calId, grilleId, champs, auteur) {
  await updateDoc(doc(db, 'emploiDuTemps', calId, 'grilles', grilleId), { ...champs, maj: new Date().toISOString() });
  if (auteur) {
    const detail = champs?.actif === true ? 'activation' : champs?.actif === false ? 'desactivation' : null;
    journaliser(calId, {
      type: 'edt', action: 'modification', detail,
      titre: auteur.titre || champs?.nom || '', par: auteur.uid || null, parNom: auteur.nom || '',
    });
  }
}

export async function supprimerGrilleEDT(calId, grilleId, auteur) {
  await deleteDoc(doc(db, 'emploiDuTemps', calId, 'grilles', grilleId));
  if (auteur) journaliser(calId, { type: 'edt', action: 'suppression', titre: auteur.titre || '', par: auteur.uid || null, parNom: auteur.nom || '' });
}

// Migration v1 → v2 (doc unique → grille), idempotente — comme MoPCal.
export async function migrerEDTSiBesoin(calId) {
  if (!calId) return;
  try {
    const snap = await getDocs(grillesRef(calId));
    if (!snap.empty) return;
    const ancien = await getDoc(doc(db, 'emploiDuTemps', calId));
    const cases = ancien.exists() ? (ancien.data().cases || {}) : {};
    if (Object.keys(cases).length === 0) return;
    await creerGrilleEDT(calId, { nom: 'Emploi du temps', cases });
  } catch { /* hors-ligne / droits : on réessaiera */ }
}

// Première grille ACTIVE dont la période contient la date.
export function grilleActiveLe(grilles, dateKey) {
  return (grilles || []).find(g => g.actif !== false && (!g.debut || g.debut <= dateKey) && (!g.fin || dateKey <= g.fin)) || null;
}

// ─── Périodicité MULTI-SEMAINES ───────────────────────────────────
// nbSemaines (défaut 1). Semaine 0 = `cases` (compat MoPCal) ; semaines
// suivantes dans `casesSemaines: { "1": {...}, "2": {...} }`.
const _lundiDe = (dk) => {
  const [y, m, d] = dk.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() - ((dt.getDay() + 6) % 7));
  return dt;
};
// Index de semaine (0-based) d'une date : écart en semaines depuis le LUNDI
// de la semaine du début de la grille, modulo la périodicité.
export function semaineDeGrille(grille, dk) {
  const n = Math.max(1, Number(grille?.nbSemaines) || 1);
  if (n === 1) return 0;
  const w = Math.round((_lundiDe(dk) - _lundiDe(grille?.debut || dk)) / (7 * 24 * 3600 * 1000));
  return ((w % n) + n) % n;
}
export const casesDeSemaine = (grille, sem) => (sem > 0
  ? ((grille?.casesSemaines || {})[String(sem)] || {})
  : (grille?.cases || {}));

// ─── Application pendant les VACANCES ─────────────────────────────
// vacApplique (global, défaut true) + vacChoix { [debutPeriode]: bool } par
// période — une période de vacances non appliquée ne génère AUCUN cours.
export function edtActifPendantVacances(grille, dk, periodes) {
  const p = (periodes || []).find(x => x.debut && x.fin && x.debut <= dk && dk <= x.fin);
  if (!p) return true;
  const choix = (grille?.vacChoix || {})[p.debut];
  return choix != null ? !!choix : (grille?.vacApplique !== false);
}

// ─── Génération À LA VOLÉE des occurrences (type 'edt') ───────────
// Jours OUVRÉS (Lun→Ven) de la fenêtre ∩ période de la grille active du jour.
// Jamais écrites en base — cohérent avec la garde et les récurrences.
const _fmtDK = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

export function genererOccurrencesEDT(grilles, debut, fin, vacances = []) {
  const map = {};
  if (!grilles || !grilles.length) return map;
  const curr = new Date(debut.getFullYear(), debut.getMonth(), debut.getDate());
  const dFin = new Date(fin.getFullYear(), fin.getMonth(), fin.getDate());
  let garde = 0;
  while (curr <= dFin && garde++ < 800) {
    const jsem = curr.getDay();                       // 0=Dim … 6=Sam
    if (jsem >= 1 && jsem <= 5) {
      const dk = _fmtDK(curr);
      const g = grilleActiveLe(grilles, dk);
      if (g && edtActifPendantVacances(g, dk, vacances)) {
        const slots = creneauxDe(g);
        const ji = jsem - 1;                          // 0=Lun … 4=Ven
        const casesSem = casesDeSemaine(g, semaineDeGrille(g, dk));
        for (let ci = 0; ci < slots.length; ci++) {
          const c = casesSem[cleCase(ji, ci)];
          if (!c || !(c.nom || c.matiere)) continue;
          const span = Math.min(Math.max(1, c.span || 1), slots.length - ci);   // borné à la fin de grille
          (map[dk] = map[dk] || []).push({
            id: `edt_${g.id}_${dk}_${ci}`,
            type: 'edt', isEdt: true, grilleId: g.id,
            titre: c.nom || c.matiere, note: c.salle || null, categorie: c.categorie || null,
            matiere: c.matiere || null, lieu: c.lieu || null, lieuId: c.lieuId || null,
            heure: c.heure || slots[ci].debut,
            heureFin: c.heureFin || slots[Math.min(ci + span - 1, slots.length - 1)].fin,
            couleurEdt: c.couleur || null,
          });
        }
      }
    }
    curr.setDate(curr.getDate() + 1);
  }
  return map;
}
